/**
 * Billing service — proxy between the front-end and the Stripe Edge Functions.
 * All calls go through `supabase.functions.invoke` to carry the auth JWT.
 *
 * This service does NOT manage state — that is the responsibility of `billing.ts` store.
 */
import { getSupabase } from './supabaseService';
import type { PlanTier, PlanCycle, Subscription, Invoice, InvoiceStatus } from '@/types/billing';
import type { QuoteConfig } from '@/types/pricingQuote';

// ── DTOs returned by Edge Functions ──────────────────────────────────────────

export type CheckoutResult = { url: string };
export type PortalResult = { url: string };
export type SubscriptionDTO = {
  plan: PlanTier;
  cycle: PlanCycle;
  status: string;
  currentPeriodEnd: string;
};
export type CancelResult = { cancelledAt: string; periodEnd: string };
export type UpcomingInvoiceResult = { amount: number | null; currency: string };

/** Raw invoice DTO as returned by the stripe-list-invoices edge function. */
export type InvoiceDTO = {
  id: string;
  created: string;
  amount_paid: number;
  status: string;
  hosted_invoice_url: string;
  invoice_pdf: string | null;
};

/** Result from configurator-checkout subscribe mode. */
export type ConfiguratorCheckoutResult = {
  url?: string;
  serverPrice: number;
  currency: string;
  amount?: number | null;
};

// ── DB row mapper ─────────────────────────────────────────────────────────────

/**
 * Maps a Supabase DB row from the `subscriptions` table to a `Subscription` type.
 * @param row - Raw DB row from the subscriptions table.
 */
export const subscriptionFromRow = (row: Record<string, unknown>): Subscription => ({
  id: row.id as string,
  workspaceId: row.workspace_id as string,
  plan: row.plan as Subscription['plan'],
  cycle: (row.cycle as Subscription['cycle']) ?? null,
  status: row.status as Subscription['status'],
  stripeCustomerId: (row.stripe_customer_id as string) ?? null,
  stripeSubscriptionId: (row.stripe_subscription_id as string) ?? null,
  stripePriceId: (row.stripe_price_id as string) ?? null,
  trialEndsAt: (row.trial_ends_at as string) ?? null,
  currentPeriodStart: (row.current_period_start as string) ?? null,
  currentPeriodEnd: (row.current_period_end as string) ?? null,
  cancelAtPeriodEnd: (row.cancel_at_period_end as boolean) ?? false,
  createdAt: row.created_at as string,
  updatedAt: row.updated_at as string,
  // Custom quota overrides from configurator checkout (H4). NULL = use plan defaults.
  maxWorkspaces: (row.max_workspaces as number) ?? null,
  maxPersonasPerDebate: (row.max_personas_per_debate as number) ?? null,
  maxSegments: (row.max_segments as number) ?? null,
  maxConcurrentDebates: (row.max_concurrent_debates as number) ?? null,
  maxCustomPersonas: (row.max_custom_personas as number) ?? null
});

// ── API calls ─────────────────────────────────────────────────────────────────

/**
 * Fetches the subscription for the currently active workspace.
 * @param workspaceId - The workspace ID to fetch.
 */
export const fetchSubscription = async (workspaceId: string): Promise<Subscription | null> => {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('workspace_id', workspaceId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // no row
    throw new Error(`Failed to fetch subscription: ${error.message}`);
  }
  return data ? subscriptionFromRow(data as Record<string, unknown>) : null;
};

/**
 * Creates a Stripe Checkout session and returns the redirect URL.
 * @param tier - The target plan tier.
 * @param cycle - The billing cycle (monthly or yearly).
 * @param options - Optional flags: returnUrl, collectCardOnly (D4 no-card trial flow).
 */
export const startCheckout = async (
  tier: Exclude<PlanTier, 'trial'>,
  cycle: PlanCycle,
  options?: { returnUrl?: string; collectCardOnly?: boolean }
): Promise<CheckoutResult> => {
  const supabase = getSupabase();
  const { data, error } = await supabase.functions.invoke('stripe-checkout', {
    body: {
      tier,
      cycle,
      returnUrl: options?.returnUrl,
      collectCardOnly: options?.collectCardOnly ?? false
    }
  });

  if (error) throw new Error(`Checkout failed: ${error.message}`);
  if (data?.error === 'already_subscribed') {
    throw Object.assign(new Error('already_subscribed'), {
      code: 'already_subscribed',
      redirectTo: data.redirectTo as string
    });
  }
  return data as CheckoutResult;
};

/**
 * Opens the Stripe Customer Portal and returns the redirect URL.
 */
export const openPortal = async (): Promise<PortalResult> => {
  const supabase = getSupabase();
  const { data, error } = await supabase.functions.invoke('stripe-portal', {
    body: {}
  });
  if (error) throw new Error(`Portal failed: ${error.message}`);
  return data as PortalResult;
};

/**
 * Updates the Stripe subscription (tier, cycle, or both).
 * At least one of targetTier or targetCycle must be provided (D8).
 * @param params - Target tier and/or cycle.
 */
export const updateSubscription = async (params: {
  targetTier?: Exclude<PlanTier, 'trial' | 'free'>;
  targetCycle?: PlanCycle;
}): Promise<SubscriptionDTO> => {
  const supabase = getSupabase();
  const { data, error } = await supabase.functions.invoke('stripe-update-subscription', {
    body: params
  });
  if (error) throw new Error(`Subscription update failed: ${error.message}`);
  return (data as { subscription: SubscriptionDTO }).subscription;
};

/**
 * Upgrades or downgrades the Stripe subscription to a new tier.
 * Rétro-compatible wrapper over updateSubscription (D8).
 * @param targetTier - The target plan tier.
 */
export const updatePlan = async (targetTier: Exclude<PlanTier, 'trial'>): Promise<SubscriptionDTO> => {
  return updateSubscription({ targetTier: targetTier as Exclude<PlanTier, 'trial' | 'free'> });
};

/**
 * Schedules cancellation of the subscription at period end.
 */
export const cancelPlan = async (): Promise<CancelResult> => {
  const supabase = getSupabase();
  const { data, error } = await supabase.functions.invoke('stripe-cancel-subscription', {
    body: {}
  });
  if (error) throw new Error(`Cancellation failed: ${error.message}`);
  return data as CancelResult;
};

/**
 * Fetches the prorated upcoming invoice amount for a plan change (tier or cycle).
 * Times out after 2 seconds (non-fatal — caller should handle null amount).
 * @param params - Target tier and/or cycle.
 */
export const fetchUpcomingInvoice = async (params: {
  targetTier?: Exclude<PlanTier, 'trial' | 'free'>;
  targetCycle?: PlanCycle;
}): Promise<UpcomingInvoiceResult> => {
  const supabase = getSupabase();

  const timeoutPromise = new Promise<UpcomingInvoiceResult>((resolve) =>
    setTimeout(() => resolve({ amount: null, currency: 'usd' }), 2000)
  );

  const fetchPromise = supabase.functions
    .invoke('stripe-upcoming-invoice', { body: params })
    .then(({ data, error }) => {
      if (error) return { amount: null, currency: 'usd' };
      return (data as UpcomingInvoiceResult) ?? { amount: null, currency: 'usd' };
    });

  return Promise.race([fetchPromise, timeoutPromise]);
};

/**
 * Fetches the list of Stripe invoices for the authenticated user's workspace.
 * Returns an empty array if no invoices or no Stripe customer.
 */
export const fetchInvoices = async (): Promise<Invoice[]> => {
  const supabase = getSupabase();
  const { data, error } = await supabase.functions.invoke('stripe-list-invoices', {
    body: {}
  });
  if (error) throw new Error(`Failed to fetch invoices: ${error.message}`);

  const dtos = ((data as { invoices: InvoiceDTO[] } | null)?.invoices ?? []);
  return dtos.map((dto): Invoice => ({
    id: dto.id,
    created: dto.created,
    amountPaid: dto.amount_paid,
    status: dto.status as InvoiceStatus,
    hostedInvoiceUrl: dto.hosted_invoice_url,
    invoicePdf: dto.invoice_pdf
  }));
};

/**
 * Calls the `configurator-checkout` edge function.
 * - mode='subscribe': returns { url, serverPrice, currency }
 * - mode='update': returns { amount, currency, serverPrice }
 * Throws typed errors for 409 (studio_or_team_active) and 422 (not_self_serve_eligible).
 * @param config - The configurator quota config.
 * @param locale - The user locale ('en' | 'fr') used to resolve currency.
 * @param mode - 'subscribe' for new plan, 'update' for existing plan modification.
 */
export const startConfiguratorCheckout = async (
  config: QuoteConfig,
  locale: 'en' | 'fr',
  mode: 'subscribe' | 'update' = 'subscribe'
): Promise<ConfiguratorCheckoutResult> => {
  const supabase = getSupabase();
  const { data, error } = await supabase.functions.invoke('configurator-checkout', {
    body: { config, locale, mode }
  });

  if (error) throw new Error(`Configurator checkout failed: ${error.message}`);

  if (data?.error === 'studio_or_team_active') {
    throw Object.assign(new Error('studio_or_team_active'), {
      code: 'studio_or_team_active',
      redirectTo: (data.redirectTo as string) ?? '/app/account#billing'
    });
  }
  if (data?.error === 'not_self_serve_eligible') {
    throw Object.assign(new Error('not_self_serve_eligible'), {
      code: 'not_self_serve_eligible'
    });
  }

  return data as ConfiguratorCheckoutResult;
};

/**
 * Convenience wrapper for `configurator-checkout` in update mode.
 * Returns the prorated amount for the PlanChangeModal.
 * @param config - The updated configurator quota config.
 * @param locale - The user locale.
 */
export const updateConfiguratorPlan = async (
  config: QuoteConfig,
  locale: 'en' | 'fr'
): Promise<ConfiguratorCheckoutResult> => {
  return startConfiguratorCheckout(config, locale, 'update');
};
